import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Image as ImageIcon, FileText, Link as LinkIcon, Video, TrendingUp, Eye, Share2, Download, Loader2, History as HistoryIcon, Filter, ArrowLeft, Moon, Sun, Brain, CreditCard, Home as HomeIcon, Menu, User, LogOut, Calendar } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ShareButton from '@/components/ShareButton';
import { generateSingleAnalysisWord, generateComparisonWord } from '@/utils/wordGenerator';
import { generatePDFFromHTML } from '@/utils/pdfGenerator';
import ScrollToTop from '@/components/ScrollToTop';

// Use environment variable with fallback to current domain
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const History = ({ darkMode: propDarkMode }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [darkMode, setDarkMode] = useState(() => {
    // Initialize from localStorage, default to true (dark mode)
    const stored = localStorage.getItem('darkMode');
    if (stored === null) {
      // If no preference stored, default to dark mode
      localStorage.setItem('darkMode', 'true');
      return true;
    }
    const isDark = stored === 'true';
    console.log('HistoryNew: Initializing darkMode =', isDark);
    return isDark;
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  console.log('HistoryNew: Current darkMode state =', darkMode);
  console.log('HistoryNew: User state =', user);

  useEffect(() => {
    // Check localStorage for dark mode preference on mount
    const storedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(storedDarkMode);
    if (storedDarkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }

    // Listen for dark mode changes from other pages/tabs
    const handleStorageChange = (e) => {
      if (e.key === 'darkMode') {
        const newDarkMode = e.newValue === 'true';
        setDarkMode(newDarkMode);
        if (newDarkMode) {
          document.body.classList.add('dark');
        } else {
          document.body.classList.remove('dark');
        }
      }
    };

    // Listen for custom dark mode events within same window
    const handleDarkModeChange = (e) => {
      const newDarkMode = e.detail;
      setDarkMode(newDarkMode);
      if (newDarkMode) {
        document.body.classList.add('dark');
      } else {
        document.body.classList.remove('dark');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('darkModeChange', handleDarkModeChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('darkModeChange', handleDarkModeChange);
    };
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [user]);

  // Redirect to login if not authenticated (with delay to allow auth to complete)
  useEffect(() => {
    // Wait a bit longer before redirecting to allow cached auth to load
    const timer = setTimeout(() => {
      if (!loading && !user) {
        console.log('🔒 User not authenticated after delay, redirecting to login');
        toast.error('Please login to view your analyses');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }
    }, 1000);  // Wait 1 second before checking

    return () => clearTimeout(timer);
  }, [user, loading, navigate]);

  const fetchHistory = async (retryCount = 0) => {
    const MAX_RETRIES = 2;
    
    try {
      // SECURITY: Only fetch if user is authenticated
      if (!user || !user.id) {
        console.log('⚠️ User not authenticated, cannot fetch analyses');
        setLoading(false);
        setAnalyses([]);
        return;
      }

      const userId = user.id;
      console.log('🔍 HistoryNew fetchHistory:', { userId, user, retryCount });

      console.log('📊 Fetching analyses from API with userId:', userId);
      
      // Get token from localStorage and add to request
      const token = localStorage.getItem('authToken');
      console.log('🔑 Token from localStorage:', token ? `exists (${token.length} chars)` : 'NOT FOUND');
      
      if (!token) {
        console.error('❌ NO TOKEN - Cannot authenticate request');
        toast.error('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      const config = {
        params: { limit: 100 },
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000  // 15 second timeout
      };
      
      console.log('✅ Sending request with Authorization header');
      
      const response = await axios.get(`${API}/analyses`, config);
      
      console.log('✅ Received analyses:', response.data.length, 'items');
      
      // Sort by timestamp, newest first
      const sorted = response.data.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      setAnalyses(sorted);
    } catch (error) {
      console.error('❌ Error fetching history:', error);
      
      // Handle specific error cases
      if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
        
        if (error.response.status === 401 || error.response.status === 403) {
          toast.error('Session expired. Please log in again.');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }
      }
      
      // Retry on network errors
      if (!error.response && retryCount < MAX_RETRIES) {
        console.log(`🔄 Retrying fetch history (attempt ${retryCount + 1}/${MAX_RETRIES})`);
        toast.info(`Retrying... (${retryCount + 1}/${MAX_RETRIES})`);
        setTimeout(() => fetchHistory(retryCount + 1), 2000);  // Retry after 2 seconds
        return;
      }
      
      toast.error('Failed to load history. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = (analysis) => {
    // Navigate to dedicated analysis detail page
    navigate(`/analysis/${analysis.id}`);
  };

  const handleDownload = async (analysis) => {
    try {
      if (analysis.is_comparison) {
        // For comparisons, we need to fetch all related analyses
        // For now, download single analysis
        await generateSingleAnalysisWord(analysis);
      } else {
        await generateSingleAnalysisWord(analysis);
      }
      toast.success('Word report downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download report');
    }
  };

  const handleDownloadPDF = async (analysis) => {
    // Navigate to analysis detail page where PDF can be generated properly
    toast.info('Opening analysis details for PDF generation...');
    navigate(`/analysis/${analysis.id}`);
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      
      if (days === 0) return 'Today';
      if (days === 1) return 'Yesterday';
      if (days < 7) return `${days} days ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return 'Unknown';
    }
  };

  const getTypeIcon = (type) => {
    const iconClass = "w-4 h-4";
    switch(type?.toLowerCase()) {
      case 'image': return <ImageIcon className={iconClass} />;
      case 'text': return <FileText className={iconClass} />;
      case 'url': return <LinkIcon className={iconClass} />;
      case 'live': return <Video className={iconClass} />;
      case 'compare': return <TrendingUp className={iconClass} />;
      default: return <FileText className={iconClass} />;
    }
  };

  const getThumbnailContent = (analysis) => {
    if (analysis.image_data) {
      return (
        <img 
          src={analysis.image_data} 
          alt="Analysis" 
          className="w-full h-full object-cover"
        />
      );
    } else if (analysis.content_type === 'video') {
      // For videos, check if image_data exists (thumbnail), otherwise show video icon
      if (analysis.image_data) {
        return (
          <div className="relative w-full h-full">
            <img 
              src={analysis.image_data} 
              alt="Video thumbnail" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <Video className="w-8 h-8 text-white" />
            </div>
          </div>
        );
      } else {
        return (
          <div className="flex flex-col items-center justify-center h-full p-3 bg-gradient-to-br from-red-100 to-pink-100">
            <Video className="w-8 h-8 text-red-600 mb-2" />
            <p className="text-xs text-gray-700 text-center">
              Video
            </p>
          </div>
        );
      }
    } else if (analysis.content_type === 'text') {
      return (
        <div className="flex items-center justify-center h-full p-3 bg-gradient-to-br from-purple-100 to-blue-100">
          <p className="text-xs text-gray-700 line-clamp-4 text-center">
            {analysis.content_preview || analysis.content || 'Text analysis'}
          </p>
        </div>
      );
    } else if (analysis.content_type === 'url') {
      return (
        <div className="flex flex-col items-center justify-center h-full p-3 bg-gradient-to-br from-blue-100 to-green-100">
          <LinkIcon className="w-8 h-8 text-blue-600 mb-2" />
          <p className="text-xs text-gray-700 text-center truncate w-full">
            {analysis.content}
          </p>
        </div>
      );
    } else if (analysis.content_type === 'document') {
      const docType = analysis.metadata?.document_type?.toUpperCase() || 'DOC';
      const fileName = analysis.content || 'Document';
      return (
        <div className="flex flex-col items-center justify-center h-full p-3 bg-gradient-to-br from-green-100 to-emerald-100">
          <FileText className="w-8 h-8 text-green-600 mb-2" />
          <div className="text-xs font-bold text-green-700 mb-1">{docType}</div>
          <p className="text-xs text-gray-700 text-center truncate w-full">
            {fileName}
          </p>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-full bg-gray-200">
        <FileText className="w-8 h-8 text-gray-400" />
      </div>
    );
  };

  // Filter analyses by type
  const filterAnalyses = (type) => {
    if (type === 'all') return analyses;
    if (type === 'compare') {
      // For compare tab, return only analyses that have comparison_group_id
      return analyses.filter(a => a.comparison_group_id);
    }
    // For other tabs, exclude comparison analyses and filter by content_type
    return analyses.filter(a => !a.comparison_group_id && a.content_type?.toLowerCase() === type.toLowerCase());
  };

  const filteredAnalyses = filterAnalyses(activeTab);

  // Group by comparison_group_id (only for 'all' and 'compare' tabs)
  const groupedAnalyses = {};
  const standaloneAnalyses = [];
  
  if (activeTab === 'all' || activeTab === 'compare') {
    // Group comparison analyses
    filteredAnalyses.forEach(analysis => {
      if (analysis.comparison_group_id) {
        if (!groupedAnalyses[analysis.comparison_group_id]) {
          groupedAnalyses[analysis.comparison_group_id] = [];
        }
        groupedAnalyses[analysis.comparison_group_id].push(analysis);
      } else if (activeTab === 'all') {
        // For 'all' tab, also show standalone analyses
        standaloneAnalyses.push(analysis);
      }
    });
  } else {
    // For specific type tabs, all are standalone (already filtered out comparisons)
    standaloneAnalyses.push(...filteredAnalyses);
  }
  
  // For "All" tab: Create unified list with comparisons and standalone, sorted by timestamp
  const allItemsForDisplay = [];
  if (activeTab === 'all') {
    // Add comparison groups (use first item's timestamp for sorting)
    Object.entries(groupedAnalyses).forEach(([groupId, groupAnalyses]) => {
      allItemsForDisplay.push({
        type: 'comparison',
        groupId,
        groupAnalyses,
        timestamp: groupAnalyses[0].timestamp
      });
    });
    // Add standalone analyses
    standaloneAnalyses.forEach(analysis => {
      allItemsForDisplay.push({
        type: 'standalone',
        analysis,
        timestamp: analysis.timestamp
      });
    });
    // Sort by timestamp (newest first)
    allItemsForDisplay.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  // Calculate counts for tabs
  const comparisonGroupsCount = new Set(analyses.filter(a => a.comparison_group_id).map(a => a.comparison_group_id)).size;
  const imageCount = analyses.filter(a => a.content_type === 'image' && !a.comparison_group_id).length;
  const textCount = analyses.filter(a => a.content_type === 'text').length;
  const urlCount = analyses.filter(a => a.content_type === 'url').length;
  const liveCount = analyses.filter(a => a.content_type === 'live').length;
  const documentCount = analyses.filter(a => a.content_type === 'document').length;

  if (loading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'} flex items-center justify-center`}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Loading history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
      {/* Header - Matches Home Page */}
      <header className={`border-b ${darkMode ? 'border-gray-700 bg-gray-800/80' : 'border-blue-100 bg-white/80'} backdrop-blur-sm sticky top-0 z-50`}>
        <div className="max-w-6xl mx-auto px-4 py-1 md:py-1.5 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate('/')}>
            <img 
              src={darkMode 
                ? "https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/p4o00bk8_Emo.zip%20-%205.png"
                : "https://customer-assets.emergentagent.com/job_credit-system-12/artifacts/3r5buf5n_Emo.zip%20-%204.png"
              }
              alt="EmoQ - Go to Home"
              title="Go to Home"
              className="h-12 md:h-14 w-auto transition-all"
            />
          </div>
          
          <div className="hidden md:flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => {
              const newMode = !darkMode;
              setDarkMode(newMode);
              localStorage.setItem('darkMode', newMode.toString());
              if (newMode) {
                document.body.classList.add('dark');
              } else {
                document.body.classList.remove('dark');
              }
              window.dispatchEvent(new CustomEvent('darkModeChange', { detail: newMode }));
            }} className="gap-2">
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="gap-2">
              <HomeIcon className="w-4 h-4" />
              Home
            </Button>
            <Button variant="ghost" size="sm" className="gap-2">
              <HistoryIcon className="w-4 h-4" />
              My Analyses
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/pricing')} className="gap-2">
              <CreditCard className="w-4 h-4" />
              Pricing
            </Button>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={user?.picture} alt={user?.name || user?.email} />
                      <AvatarFallback>
                        {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden lg:inline">{user?.name || user?.email}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>
                    <div className="text-sm">
                      <div>{user?.name || 'User'}</div>
                      <div className="text-xs text-gray-500">{user?.email}</div>
                      <div className="text-xs font-semibold mt-1">Credits: {user?.credits || 0}</div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="w-4 h-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => logout()}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="gap-2">
                <User className="w-4 h-4" />
                Login
              </Button>
            )}
          </div>

          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <Menu className={`w-6 h-6 ${darkMode ? 'text-white' : 'text-gray-900'}`} />
          </button>
        </div>

        {mobileMenuOpen && (
          <div className={`md:hidden border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-blue-100 bg-white'}`}>
            <div className="px-4 py-2 space-y-2">
              <Button variant="ghost" size="sm" onClick={() => {
                const newMode = !darkMode;
                setDarkMode(newMode);
                localStorage.setItem('darkMode', newMode.toString());
                if (newMode) {
                  document.body.classList.add('dark');
                } else {
                  document.body.classList.remove('dark');
                }
                window.dispatchEvent(new CustomEvent('darkModeChange', { detail: newMode }));
              }} className="w-full justify-start gap-2">
                {darkMode ? <><Sun className="w-4 h-4" />Light Mode</> : <><Moon className="w-4 h-4" />Dark Mode</>}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                <HomeIcon className="w-4 h-4" />
                Home
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setMobileMenuOpen(false)} className="w-full justify-start gap-2">
                <HistoryIcon className="w-4 h-4" />
                My Analyses
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                <CreditCard className="w-4 h-4" />
                Pricing
              </Button>
              
              {user ? (
                <>
                  <div className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                    <div className="font-semibold">{user?.name || user?.email}</div>
                    <div className="text-xs text-gray-500">Credits: {user?.credits || 0}</div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="w-full justify-start gap-2">
                    <User className="w-4 h-4" />
                    My Profile
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => logout()} className="w-full justify-start gap-2">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => navigate('/login')} className="w-full justify-start gap-2">
                  <User className="w-4 h-4" />
                  Login
                </Button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content with Dark Mode Support */}
      <div className={`max-w-7xl mx-auto px-4 py-8 ${darkMode ? 'dark' : ''}`}>
        {/* Page Title */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            My Analyses
          </h1>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {analyses.length} {analyses.length === 1 ? 'analysis' : 'analyses'} • View, download, or share any report
          </p>
        </div>

        {/* Filter Tabs - Matching Home Page Colors */}
        <div className="mb-6">
          <div className="flex gap-2 flex-wrap justify-center md:justify-start">
            {/* All Tab - Blue */}
            <Button 
              onClick={() => setActiveTab('all')}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className={`text-xs md:text-sm px-3 py-2 transition-all duration-200 transform hover:scale-105 hover:-translate-y-0.5 active:scale-95 ${
                activeTab === 'all'
                  ? '!bg-blue-600 !text-white !border-blue-600 shadow-lg shadow-blue-500/50'
                  : 'bg-transparent text-blue-700 dark:text-blue-300 border-2 border-blue-300 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 shadow-sm hover:shadow-md hover:shadow-blue-300/30'
              }`}
            >
              All ({analyses.length})
            </Button>
            
            {/* Image Tab - Cyan */}
            <Button 
              onClick={() => setActiveTab('image')}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className={`text-xs md:text-sm px-3 py-2 gap-1 transition-all duration-200 transform hover:scale-105 hover:-translate-y-0.5 active:scale-95 ${
                activeTab === 'image'
                  ? '!bg-cyan-600 !text-white !border-cyan-600 shadow-lg shadow-cyan-500/50'
                  : 'bg-transparent text-cyan-700 dark:text-cyan-300 border-2 border-cyan-300 hover:border-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 shadow-sm hover:shadow-md hover:shadow-cyan-300/30'
              }`}
            >
              <ImageIcon className="w-3 h-3" />
              Image ({imageCount})
            </Button>
            
            {/* Text Tab - Indigo */}
            <Button 
              onClick={() => setActiveTab('text')}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className={`text-xs md:text-sm px-3 py-2 gap-1 transition-all duration-200 transform hover:scale-105 hover:-translate-y-0.5 active:scale-95 ${
                activeTab === 'text'
                  ? '!bg-indigo-600 !text-white !border-indigo-600 shadow-lg shadow-indigo-500/50'
                  : 'bg-transparent text-indigo-700 dark:text-indigo-300 border-2 border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 shadow-sm hover:shadow-md hover:shadow-indigo-300/30'
              }`}
            >
              <FileText className="w-3 h-3" />
              Text ({textCount})
            </Button>
            
            {/* URL Tab - Purple */}
            <Button 
              onClick={() => setActiveTab('url')}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className={`text-xs md:text-sm px-3 py-2 gap-1 transition-all duration-200 transform hover:scale-105 hover:-translate-y-0.5 active:scale-95 ${
                activeTab === 'url'
                  ? '!bg-purple-600 !text-white !border-purple-600 shadow-lg shadow-purple-500/50'
                  : 'bg-transparent text-purple-700 dark:text-purple-300 border-2 border-purple-300 hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 shadow-sm hover:shadow-md hover:shadow-purple-300/30'
              }`}
            >
              <LinkIcon className="w-3 h-3" />
              URL ({urlCount})
            </Button>
            
            {/* Document Tab - Green */}
            <Button 
              onClick={() => setActiveTab('document')}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className={`text-xs md:text-sm px-3 py-2 gap-1 transition-all duration-200 transform hover:scale-105 hover:-translate-y-0.5 active:scale-95 ${
                activeTab === 'document'
                  ? '!bg-green-600 !text-white !border-green-600 shadow-lg shadow-green-500/50'
                  : 'bg-transparent text-green-700 dark:text-green-300 border-2 border-green-300 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 shadow-sm hover:shadow-md hover:shadow-green-300/30'
              }`}
            >
              <FileText className="w-3 h-3" />
              Document ({documentCount})
            </Button>
            
            {/* Video Tab - Red (NEW!) */}
            <Button 
              onClick={() => setActiveTab('video')}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className={`text-xs md:text-sm px-3 py-2 gap-1 transition-all duration-200 transform hover:scale-105 hover:-translate-y-0.5 active:scale-95 ${
                activeTab === 'video'
                  ? '!bg-red-600 !text-white !border-red-600 shadow-lg shadow-red-500/50'
                  : 'bg-transparent text-red-700 dark:text-red-300 border-2 border-red-300 hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 shadow-sm hover:shadow-md hover:shadow-red-300/30'
              }`}
            >
              <Video className="w-3 h-3" />
              Video ({analyses.filter(a => a.content_type === 'video').length})
            </Button>
            
            {/* Compare Tab - Teal */}
            <Button 
              onClick={() => setActiveTab('compare')}
              style={{ WebkitTapHighlightColor: 'transparent' }}
              className={`text-xs md:text-sm px-3 py-2 gap-1 transition-all duration-200 transform hover:scale-105 hover:-translate-y-0.5 active:scale-95 ${
                activeTab === 'compare'
                  ? '!bg-teal-600 !text-white !border-teal-600 shadow-lg shadow-teal-500/50'
                  : 'bg-transparent text-teal-700 dark:text-teal-300 border-2 border-teal-300 hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20 shadow-sm hover:shadow-md hover:shadow-teal-300/30'
              }`}
            >
              <ImageIcon className="w-3 h-3" />
              Compare ({comparisonGroupsCount})
            </Button>
          </div>
        </div>

        {/* Empty State */}
        {filteredAnalyses.length === 0 && (
          <Card 
            className={darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}
            style={darkMode ? { backgroundColor: '#1f2937 !important', borderColor: '#374151' } : { backgroundColor: '#ffffff' }}
          >
            <CardContent className="py-12 text-center">
              <FileText className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                No analyses yet
              </h3>
              <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Start analyzing content to see your history here
              </p>
              <Button onClick={() => navigate('/')}>
                Start Analyzing
              </Button>
            </CardContent>
          </Card>
        )}

        )}

        {/* For "All" tab: Show unified chronological list */}
        {activeTab === 'all' && allItemsForDisplay.length > 0 && (
          <div className="space-y-4 mb-8">
            {allItemsForDisplay.map((item, idx) => (
              item.type === 'comparison' ? (
                // Comparison Group Card
                <Card key={item.groupId} className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      {/* Thumbnails */}
                      <div className="flex gap-2 flex-wrap">
                        {item.groupAnalyses.slice(0, 3).map((analysis, idx) => (
                          <div key={idx} className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 border-purple-400 flex-shrink-0">
                            {getThumbnailContent(analysis)}
                          </div>
                        ))}
                        {item.groupAnalyses.length > 3 && (
                          <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-lg flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <span className="text-xs sm:text-sm font-bold">+{item.groupAnalyses.length - 3}</span>
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="flex items-center gap-1 bg-purple-600 text-white">
                            <TrendingUp className="w-3 h-3" />
                            Compare
                          </Badge>
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {item.groupAnalyses.length} images
                          </span>
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {formatDate(item.groupAnalyses[0].timestamp)}
                          </span>
                        </div>
                        {item.groupAnalyses[0].custom_question && (
                          <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <span className="font-semibold">Purpose:</span> {item.groupAnalyses[0].custom_question}
                          </p>
                        )}
                      </div>
                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm"
                          onClick={() => handleViewReport(item.groupAnalyses[0])}
                          className="flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Button>
                        <ShareButton 
                          analysisId={item.groupAnalyses[0].id}
                          userName="User"
                          size="sm"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                // Standalone Analysis Card
                <Card 
                  key={item.analysis.id} 
                  className={`overflow-hidden hover:shadow-lg transition-shadow ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}
                  style={darkMode ? { backgroundColor: '#1f2937', borderColor: '#374151' } : { backgroundColor: '#ffffff' }}
                >
                  <CardContent className="p-0 flex flex-col sm:flex-row">
                    {/* Thumbnail */}
                    <div className="w-full sm:w-48 h-40 overflow-hidden flex-shrink-0">
                      {getThumbnailContent(item.analysis)}
                    </div>
                    {/* Content */}
                    <div className="p-4 flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="flex items-center gap-1">
                          {getTypeIcon(item.analysis.content_type)}
                          {item.analysis.content_type}
                        </Badge>
                        <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatDate(item.analysis.timestamp)}
                        </span>
                      </div>
                      <div className="mb-3">
                        <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {item.analysis.dominant_emotion}
                        </div>
                        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Valence: {Math.round((item.analysis.valence || 0) * 100)}%
                        </div>
                      </div>
                      {/* Action Buttons */}
                      <div className="flex gap-2 flex-wrap">
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewReport(item.analysis)}
                          className="flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadPDF(item.analysis)}
                          className="flex items-center gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          PDF
                        </Button>
                        <ShareButton 
                          analysisId={item.analysis.id}
                          userName="User"
                          size="sm"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            ))}
          </div>
        )}

        {/* Regular Analyses - Grid Layout (For specific tabs) */}
        {activeTab !== 'all' && standaloneAnalyses.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
            {standaloneAnalyses.map((analysis) => (
              <Card 
                key={analysis.id} 
                className={`overflow-hidden hover:shadow-lg transition-shadow ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}
                style={darkMode ? { backgroundColor: '#1f2937', borderColor: '#374151' } : { backgroundColor: '#ffffff' }}
              >
                <CardContent className="p-0">
                  {/* Thumbnail */}
                  <div className="h-40 overflow-hidden">
                    {getThumbnailContent(analysis)}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="flex items-center gap-1">
                        {getTypeIcon(analysis.content_type)}
                        {analysis.content_type}
                      </Badge>
                      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {formatDate(analysis.timestamp)}
                      </span>
                    </div>

                    <div className="mb-3">
                      <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {analysis.dominant_emotion}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Valence: {Math.round((analysis.valence || 0) * 100)}%
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-3 gap-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewReport(analysis)}
                        className="flex items-center justify-center gap-1"
                      >
                        <Eye className="w-3 h-3" />
                        View & Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadPDF(analysis)}
                        className="flex items-center justify-center gap-1"
                        title="Download PDF"
                      >
                        <FileText className="w-3 h-3" />
                        PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(analysis)}
                        className="flex items-center justify-center gap-1"
                        title="Download Word"
                      >
                        <Download className="w-3 h-3" />
                        Word
                      </Button>
                    </div>
                    <div className="mt-2">
                      <ShareButton 
                        analysisId={analysis.id}
                        userName="User"
                        size="sm"
                        className="w-full"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {activeTab === 'compare' && Object.keys(groupedAnalyses).length > 0 && (
          <div className="mb-8">
            <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Compare Analyses
            </h2>
            <div className="grid gap-4">
              {Object.entries(groupedAnalyses).map(([groupId, groupAnalyses]) => (
                <Card key={groupId} className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                      {/* Thumbnails */}
                      <div className="flex gap-2 flex-wrap">
                        {groupAnalyses.slice(0, 3).map((analysis, idx) => (
                          <div key={idx} className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 border-yellow-400 flex-shrink-0">
                            {getThumbnailContent(analysis)}
                          </div>
                        ))}
                        {groupAnalyses.length > 3 && (
                          <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-lg flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <span className="text-xs sm:text-sm font-bold">+{groupAnalyses.length - 3}</span>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            Comparison
                          </Badge>
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {groupAnalyses.length} images
                          </span>
                          <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {formatDate(groupAnalyses[0].timestamp)}
                          </span>
                        </div>
                        {groupAnalyses[0].custom_question && (
                          <p className={`text-sm mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <span className="font-semibold">Purpose:</span> {groupAnalyses[0].custom_question}
                          </p>
                        )}
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Winner: Image #{groupAnalyses.findIndex(a => a === groupAnalyses.sort((a,b) => b.valence - a.valence)[0]) + 1}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2">
                        <Button 
                          size="sm"
                          onClick={() => handleViewReport(groupAnalyses[0])}
                          className="flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Button>
                        <ShareButton 
                          analysisId={groupAnalyses[0].id}
                          userName="User"
                          size="sm"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Scroll to Top Button */}
      <ScrollToTop darkMode={darkMode} />
    </div>
  );
};

export default History;
